import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import "../styles/Installers.css";

// 🌍 Haversine formula for distance in kilometers
const getDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
};

const API_BASE = "https://globisun-app.onrender.com"; // ⚡ Live backend URL

export default function Installers() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [installers, setInstallers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [modalImage, setModalImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userLocation] = useState({ lat: 6.5244, lon: 3.3792 }); // Default: Lagos

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (!["individual", "vendor"].includes(user.role)) {
      navigate("/dashboard/" + user.role);
      return;
    }

    const fetchInstallers = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/services/installer/approved`);
        if (!res.ok) throw new Error("Failed to fetch installers");
        const data = await res.json();

        const withDistance = data.map((inst) => {
          const lat = inst.lat ?? 6.5244;
          const lon = inst.lon ?? 3.3792;
          const dist = getDistance(userLocation.lat, userLocation.lon, lat, lon);
          return { ...inst, distance: Math.round(dist * 10) / 10 };
        });

        setInstallers(withDistance);
        setFiltered(withDistance);
      } catch (err) {
        console.error("❌ Failed to fetch installers:", err.message);
        setError("Could not load installers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInstallers();
  }, [user, navigate, userLocation]);

  useEffect(() => {
    const result = installers.filter(
      (i) =>
        i.title.toLowerCase().includes(search.toLowerCase()) &&
        (!cityFilter ||
          (i.location && i.location.toLowerCase().includes(cityFilter.toLowerCase())))
    );
    setFiltered(result);
  }, [search, cityFilter, installers]);

  return (
    <div className="installers-page">
      <header className="hero">
        <h1>🔆 Trusted Solar Installers</h1>
        <div className="ticker">
          <ul>
            <li>Certified by Industry Standards</li>
            <li>Background Checked</li>
            <li>Top Customer Ratings</li>
            <li>Technical Audited</li>
            <li>Fully Verified</li>
          </ul>
        </div>
      </header>

      <div className="filters">
        <input
          type="text"
          placeholder="🔍 Search installer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          type="text"
          placeholder="🏙️ Filter by city"
          value={cityFilter}
          onChange={(e) => setCityFilter(e.target.value)}
        />
      </div>

      {loading ? (
        <p>⏳ Loading installers...</p>
      ) : error ? (
        <p className="error-msg">❌ {error}</p>
      ) : (
        <div className="installer-grid">
          {filtered.map((installer) => (
            <div className="card" key={installer._id}>
              <div
                className="image-container"
                onClick={() =>
                  setModalImage(`${API_BASE}${installer.image}`)
                }
              >
                <img src={`${API_BASE}${installer.image}`} alt="Installer" />
              </div>

              <div className="card-content">
                <h3>{installer.title}</h3>
                <p>
                  📍 {installer.location || "Unknown"} • {installer.distance} km away
                </p>
                <p>
                  🔧 {installer.experience || 0} yrs • {installer.category}
                </p>
                <p>
                  ⭐ {installer.rating || "4.5"} • {installer.reviews || 12} reviews
                </p>

                <button
                  onClick={() => window.open(`tel:${installer.contact}`)}
                  className="contact-btn"
                >
                  📞 Contact
                </button>

                {installer.uploader && (
                  <button
                    onClick={() => navigate(`/chat/${installer.uploader}`)}
                    className="chat-btn"
                  >
                    💬 Chat Installer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalImage && (
        <div className="modal" onClick={() => setModalImage(null)}>
          <img src={modalImage} alt="Full Installer" />
        </div>
      )}

      <a
        className="floating-cta"
        href="https://wa.me/2349012345678"
        target="_blank"
        rel="noopener noreferrer"
      >
        💬 Chat on WhatsApp
      </a>
    </div>
  );
}
